const EPSILON = 0.005;

export const parsePolishNumber = (value) => {
  if (typeof value === "number") return value;
  const normalized = String(value ?? "")
    .replace(/\s/g, "")
    .replace(",", ".");
  return normalized === "" ? Number.NaN : Number(normalized);
};

export const calculatePayment = (principal, monthlyRate, months) => {
  if (principal <= 0 || months <= 0) return 0;
  if (monthlyRate === 0) return principal / months;
  // Stable also for very small positive rates (avoids subtracting near-equal numbers).
  return (principal * monthlyRate) / -Math.expm1(-months * Math.log1p(monthlyRate));
};

const roundMoney = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const simulateLoan = ({
  principal,
  months,
  annualRate,
  monthlyOverpayment = 0,
  monthlyOverpaymentStart = 1,
  monthlyOverpaymentMonths = 0,
  oneTimeOverpayment = 0,
  oneTimeOverpaymentMonth = 1,
  strategy = "shorten",
  paymentType = "equal",
}) => {
  if (!["equal", "decreasing"].includes(paymentType)) throw new Error("Nieznany rodzaj rat.");
  const monthlyRate = annualRate / 100 / 12;
  const originalCapital = principal / months;
  const originalPayment = paymentType === "decreasing"
    ? originalCapital + principal * monthlyRate
    : calculatePayment(principal, monthlyRate, months);
  let balance = principal;
  let totalInterest = 0;
  let month = 0;
  let oneTimeApplied = 0;
  let overpaymentOccurred = false;
  let firstPaymentAfterOverpayment = null;
  const schedule = [];

  while (balance > EPSILON && month < 1200) {
    month += 1;
    const oneTime = month === oneTimeOverpaymentMonth
      ? Math.min(oneTimeOverpayment, balance)
      : 0;
    balance = Math.max(0, balance - oneTime);
    oneTimeApplied += oneTime;

    if (balance <= EPSILON) {
      schedule.push({
        month,
        scheduledPayment: 0,
        overpayment: roundMoney(oneTime),
        interest: 0,
        principal: roundMoney(oneTime),
        balance: 0,
        totalPayment: roundMoney(oneTime),
      });
      break;
    }

    const monthsLeft = Math.max(1, months - month + 1);
    const scheduledPayment = paymentType === "decreasing"
      ? (strategy === "lowerPayment" ? balance / monthsLeft : originalCapital) + balance * monthlyRate
      : strategy === "lowerPayment"
        ? calculatePayment(balance, monthlyRate, monthsLeft)
        : originalPayment;
    const interest = balance * monthlyRate;
    const principalFromPayment = Math.min(
      balance,
      Math.max(0, scheduledPayment - interest)
    );
    const monthlyOverpaymentEnd = monthlyOverpaymentMonths > 0
      ? monthlyOverpaymentStart + monthlyOverpaymentMonths - 1
      : Number.POSITIVE_INFINITY;
    const plannedMonthlyExtra = month >= monthlyOverpaymentStart && month <= monthlyOverpaymentEnd
      ? monthlyOverpayment
      : 0;
    const monthlyExtra = Math.min(
      plannedMonthlyExtra,
      Math.max(0, balance - principalFromPayment)
    );
    const actualPayment = oneTime + interest + principalFromPayment + monthlyExtra;

    if (!firstPaymentAfterOverpayment && (overpaymentOccurred || oneTime > 0)) {
      firstPaymentAfterOverpayment = {
        month,
        amount: roundMoney(interest + principalFromPayment),
      };
    }
    overpaymentOccurred = overpaymentOccurred || oneTime > 0 || monthlyExtra > 0;

    balance = Math.max(0, balance - principalFromPayment - monthlyExtra);
    totalInterest += interest;

    schedule.push({
      month,
      scheduledPayment: roundMoney(interest + principalFromPayment),
      overpayment: roundMoney(oneTime + monthlyExtra),
      interest: roundMoney(interest),
      principal: roundMoney(oneTime + principalFromPayment + monthlyExtra),
      balance: roundMoney(balance),
      totalPayment: roundMoney(actualPayment),
    });

    if (strategy === "lowerPayment" && month >= months && balance > EPSILON) {
      const finalInterest = balance * monthlyRate;
      totalInterest += finalInterest;
      month += 1;
      schedule.push({
        month,
        scheduledPayment: roundMoney(balance + finalInterest),
        overpayment: 0,
        interest: roundMoney(finalInterest),
        principal: roundMoney(balance),
        balance: 0,
        totalPayment: roundMoney(balance + finalInterest),
      });
      balance = 0;
    }
  }

  if (month >= 1200 && balance > EPSILON) {
    throw new Error("Nie udało się wyliczyć harmonogramu dla podanych danych.");
  }

  const lastOverpaymentIndex = schedule.reduce((last, row, index) => row.overpayment > 0 ? index : last, -1);
  const paymentAfterLastOverpayment = lastOverpaymentIndex >= 0 ? schedule[lastOverpaymentIndex + 1] ?? null : null;

  return {
    months: schedule.length,
    originalPayment: roundMoney(originalPayment),
    firstScheduledPayment: schedule[0]?.scheduledPayment ?? 0,
    firstPaymentAfterOverpayment,
    paymentAfterLastOverpayment: paymentAfterLastOverpayment
      ? {month: paymentAfterLastOverpayment.month, amount: paymentAfterLastOverpayment.scheduledPayment}
      : null,
    totalInterest: roundMoney(totalInterest),
    oneTimeOverpayment: roundMoney(oneTimeApplied),
    schedule,
  };
};

export const calculateScenarios = (input) => {
  const baseline = simulateLoan({ ...input, strategy: "shorten", monthlyOverpayment: 0, oneTimeOverpayment: 0 });
  const shorten = simulateLoan({ ...input, strategy: "shorten" });
  const lowerPayment = simulateLoan({ ...input, strategy: "lowerPayment" });

  return {
    baseline,
    shorten,
    lowerPayment,
    shortenSavings: roundMoney(baseline.totalInterest - shorten.totalInterest),
    lowerPaymentSavings: roundMoney(baseline.totalInterest - lowerPayment.totalInterest),
    shortenMonthsSaved: Math.max(0, baseline.months - shorten.months),
    lowerPaymentMonthsSaved: Math.max(0, baseline.months - lowerPayment.months),
  };
};

export const validateInput = (input) => {
  const errors = {};
  if (input.paymentType !== undefined && !["equal", "decreasing"].includes(input.paymentType)) {
    errors.paymentType = "Wybierz raty równe albo malejące.";
  }
  if (!Number.isFinite(input.principal) || input.principal <= 0) {
    errors.principal = "Wpisz saldo kredytu większe od 0 zł.";
  }
  if (input.principal > 1e9) {
    errors.principal = "Kalkulator obsługuje saldo do 1 mld zł.";
  }
  if (!Number.isInteger(input.months) || input.months < 1 || input.months > 600) {
    errors.months = "Podaj okres od 1 do 600 miesięcy.";
  }
  if (!Number.isFinite(input.annualRate) || input.annualRate < 0 || input.annualRate > 30) {
    errors.annualRate = "Podaj oprocentowanie od 0% do 30%.";
  }
  if (!Number.isFinite(input.monthlyOverpayment) || input.monthlyOverpayment < 0) {
    errors.monthlyOverpayment = "Nadpłata miesięczna nie może być ujemna.";
  }
  if (!Number.isInteger(input.monthlyOverpaymentStart) || input.monthlyOverpaymentStart < 1 || input.monthlyOverpaymentStart > input.months) {
    errors.monthlyOverpaymentStart = "Podaj miesiąc od 1 do końca okresu kredytu.";
  }
  if (!Number.isInteger(input.monthlyOverpaymentMonths) || input.monthlyOverpaymentMonths < 0 || input.monthlyOverpaymentMonths > input.months) {
    errors.monthlyOverpaymentMonths = "Podaj liczbę miesięcy od 1 do końca okresu albo 0.";
  }
  if (!Number.isFinite(input.oneTimeOverpayment) || input.oneTimeOverpayment < 0) {
    errors.oneTimeOverpayment = "Nadpłata jednorazowa nie może być ujemna.";
  } else if (Number.isFinite(input.principal) && input.oneTimeOverpayment > input.principal) {
    errors.oneTimeOverpayment = "Nadpłata jednorazowa nie może przekraczać salda kredytu.";
  }
  if (!Number.isInteger(input.oneTimeOverpaymentMonth) || input.oneTimeOverpaymentMonth < 1 || input.oneTimeOverpaymentMonth > input.months) {
    errors.oneTimeOverpaymentMonth = "Podaj miesiąc od 1 do końca okresu kredytu.";
  }
  return errors;
};

